import React from 'react';
import { BlurredMedia } from '@/components/BlurredMedia';
import { getImageUrl } from '@/lib/utils/image';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import { Badge } from '@/components/ui/badge';
import { Crown, Shield } from 'lucide-react';

type GalleryItem = {
  id: number | string;
  url: string;
  thumbnail_url?: string;
  is_fsk18?: boolean;
  is_profile_image?: boolean;
  should_blur?: boolean;
};

interface ProfileGalleryCarouselProps {
  characterName: string;
  galleryImages: GalleryItem[];
  characterId?: number;
  t: any;
  onImageClick: (imageUrl: string, isFsk18: boolean, originalIndex: number) => void;
}

export const ProfileGalleryCarousel: React.FC<ProfileGalleryCarouselProps> = ({
  characterName,
  galleryImages,
  characterId,
  t,
  onImageClick,
}) => {
  // Slides: alle Bilder, Anzeige 3/2/1 je nach Breakpoint per basis-Classes
  return (
    <div className="relative h-full overflow-hidden rounded-token-card">
      <Carousel
        className="h-full"
        opts={{ loop: galleryImages.length > 3, duration: 10 }}
      >
        <CarouselContent className="h-full items-center ml-0">
          {galleryImages.map((image, originalIndex) => (
            <CarouselItem
              key={image.id}
              className="h-full basis-full md:basis-1/2 lg:basis-1/3"
            >
              <div
                className="gallery-item cursor-pointer h-full"
                onClick={() => onImageClick(getImageUrl(image.url), Boolean(image.is_fsk18), originalIndex)}
              >
                <BlurredMedia
                  src={getImageUrl(image.url)}
                  alt={t('profile.galleryImageAlt', { name: characterName })}
                  shouldBlur={Boolean(image.should_blur)}
                  isFsk18={Boolean(image.is_fsk18)}
                  characterId={characterId}
                  containerClassName="w-full h-full"
                  className="w-full h-full object-cover object-center"
                  autoPlayVideo
                  showMuteToggle
                  {...(image.thumbnail_url ? { poster: getImageUrl(image.thumbnail_url) } : {})}
                />
                {/* FSK18 Badge - styled like CharacterCard */}
                {image.is_fsk18 && (
                  <Badge
                    className="absolute top-token-sm right-token-sm font-bold text-xs uppercase tracking-wide px-token-xs py-token-xs shadow-token-card flex items-center"
                    style={{
                      background: 'linear-gradient(135deg, var(--color-error) 0%, var(--color-error-dark) 100%)',
                      color: 'white',
                      border: '1px solid var(--color-white-30)',
                      boxShadow: '0 2px 8px rgba(var(--color-error-rgb), 0.4)'
                    }}
                  >
                    <Shield className="h-3 w-3 mr-token-xs" />
                    {t('profile.fsk18Short')}
                  </Badge>
                )}

                {image.is_profile_image && (
                  <Badge className="absolute top-token-sm left-token-sm bg-warning text-xs">
                    <Crown className="h-3 w-3 mr-token-xs" />
                    {t('profile.profile')}
                  </Badge>
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        {galleryImages.length > 1 && (
          <>
            <CarouselPrevious className="left-2 bg-black/60 text-white hover:bg-black/70 border-none" />
            <CarouselNext className="right-2 bg-black/60 text-white hover:bg-black/70 border-none" />
          </>
        )}
      </Carousel>
    </div>
  );
};

export default ProfileGalleryCarousel;


